import React, { useState } from 'react';
import { 
  Accessibility, 
  Calendar, 
  DollarSign, 
  FileText, 
  Users, 
  Plus, 
  Search, 
  Filter,
  Edit,
  Eye,
  Download,
  AlertCircle,
  CheckCircle,
  Clock,
  Target,
  Phone,
  Mail,
  Star
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useNavigate } from 'react-router-dom';

interface NDISPlan {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  budget: number;
  status: 'active' | 'expired' | 'pending';
  participant: string;
}

interface NDISGoal {
  id: string;
  title: string;
  category: string;
  targetDate: string;
  progress: number;
  status: 'in_progress' | 'completed' | 'not_started';
  description: string;
}

interface NDISProvider {
  id: string;
  name: string;
  service: string;
  contact: string;
  email: string;
  status: 'active' | 'inactive';
  rating: number;
}

export function MobileNDISDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'plans' | 'goals' | 'providers'>('overview');
  const [searchQuery, setSearchQuery] = useState('');

  // Mock data
  const ndisPlans: NDISPlan[] = [
    {
      id: '1',
      title: 'Core Supports Plan',
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      budget: 25000,
      status: 'active',
      participant: 'John Smith'
    },
    {
      id: '2',
      title: 'Capacity Building Plan',
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      budget: 15000,
      status: 'active',
      participant: 'John Smith'
    }
  ];

  const ndisGoals: NDISGoal[] = [
    {
      id: '1',
      title: 'Improve Communication Skills',
      category: 'Communication',
      targetDate: '2024-06-30',
      progress: 75,
      status: 'in_progress',
      description: 'Work with speech therapist to improve verbal communication'
    },
    {
      id: '2',
      title: 'Increase Community Participation',
      category: 'Social',
      targetDate: '2024-08-31',
      progress: 45,
      status: 'in_progress',
      description: 'Join community groups and participate in local activities'
    },
    {
      id: '3',
      title: 'Develop Daily Living Skills',
      category: 'Daily Living',
      targetDate: '2024-12-31',
      progress: 20,
      status: 'in_progress',
      description: 'Learn independent living skills with support worker'
    }
  ];

  const ndisProviders: NDISProvider[] = [
    {
      id: '1',
      name: 'ABC Therapy Services',
      service: 'Occupational Therapy',
      contact: '(555) 123-4567',
      email: 'info@abctherapy.com',
      status: 'active',
      rating: 4.5
    },
    {
      id: '2',
      name: 'Community Support Plus',
      service: 'Support Coordination',
      contact: '(555) 234-5678',
      email: 'contact@communitysupport.com',
      status: 'active',
      rating: 4.8
    },
    {
      id: '3',
      name: 'Life Skills Development',
      service: 'Daily Living Support',
      contact: '(555) 345-6789',
      email: 'info@lifeskills.com',
      status: 'active',
      rating: 4.2
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'in_progress':
        return 'text-green-600 bg-green-100';
      case 'pending':
      case 'not_started':
        return 'text-yellow-600 bg-yellow-100';
      case 'expired':
      case 'completed':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const totalBudget = ndisPlans.reduce((sum, plan) => sum + plan.budget, 0);
  const usedBudget = 18500; // Mock used budget
  const remainingBudget = totalBudget - usedBudget;
  const budgetUtilization = (usedBudget / totalBudget) * 100;

  const renderOverview = () => (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign size={16} className="text-green-500" />
            <h3 className="font-medium text-gray-900">Total Budget</h3>
          </div>
          <p className="text-2xl font-bold text-gray-900">${totalBudget.toLocaleString()}</p>
          <p className="text-xs text-gray-500">Annual allocation</p>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Target size={16} className="text-blue-500" />
            <h3 className="font-medium text-gray-900">Goals Progress</h3>
          </div>
          <p className="text-2xl font-bold text-gray-900">{ndisGoals.length}</p>
          <p className="text-xs text-gray-500">Active goals</p>
        </Card>
      </div>

      {/* Budget Progress */}
      <Card className="p-4">
        <h3 className="font-medium text-gray-900 mb-3">Budget Utilization</h3>
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Used</span>
            <span className="font-medium">${usedBudget.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Remaining</span>
            <span className="font-medium">${remainingBudget.toLocaleString()}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-idecide-primary h-2 rounded-full" 
              style={{ width: `${budgetUtilization}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500">{budgetUtilization.toFixed(1)}% utilized</p>
        </div>
      </Card>

      {/* Recent Goals */}
      <div>
        <h3 className="font-medium text-gray-900 mb-3">Recent Goals</h3>
        <div className="space-y-2">
          {ndisGoals.slice(0, 2).map((goal) => (
            <div key={goal.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <p className="font-medium text-gray-900">{goal.title}</p>
                <p className="text-sm text-gray-600">{goal.category}</p>
              </div>
              <div className="text-right">
                <p className="font-medium text-gray-900">{goal.progress}%</p>
                <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(goal.status)}`}>
                  {goal.status.replace('_', ' ')}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderPlans = () => (
    <div className="space-y-3">
      {ndisPlans.map((plan) => (
        <Card key={plan.id} className="p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{plan.title}</h3>
                <p className="text-sm text-gray-600">{plan.participant}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(plan.status)}`}>
                {plan.status}
              </span>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Budget:</span>
                <span className="font-medium text-gray-900">${plan.budget.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Period:</span>
                <span className="font-medium text-gray-900">{plan.startDate} - {plan.endDate}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1">
                <Eye size={14} className="mr-1" />
                View
              </Button>
              <Button size="sm" variant="outline" className="flex-1">
                <Edit size={14} className="mr-1" />
                Edit
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  const renderGoals = () => (
    <div className="space-y-3">
      {ndisGoals.map((goal) => (
        <Card key={goal.id} className="p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{goal.title}</h3>
                <p className="text-sm text-gray-600">{goal.category}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(goal.status)}`}>
                {goal.status.replace('_', ' ')}
              </span>
            </div>
            <p className="text-sm text-gray-600">{goal.description}</p>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Progress:</span>
                <span className="font-medium text-gray-900">{goal.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-idecide-primary h-2 rounded-full" 
                  style={{ width: `${goal.progress}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Target:</span>
                <span className="font-medium text-gray-900">{goal.targetDate}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1">
                <Eye size={14} className="mr-1" />
                View
              </Button>
              <Button size="sm" variant="outline" className="flex-1">
                <Edit size={14} className="mr-1" />
                Edit
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  const renderProviders = () => (
    <div className="space-y-3">
      {ndisProviders.map((provider) => (
        <Card key={provider.id} className="p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{provider.name}</h3>
                <p className="text-sm text-gray-600">{provider.service}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(provider.status)}`}>
                {provider.status}
              </span>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone size={14} />
                <span>{provider.contact}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail size={14} />
                <span>{provider.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Star size={14} />
                <span>{provider.rating}/5 rating</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1">
                <Eye size={14} className="mr-1" />
                View
              </Button>
              <Button size="sm" variant="outline" className="flex-1">
                <Edit size={14} className="mr-1" />
                Edit
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="p-4 pb-safe space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">NDIS Management</h2>
          <p className="text-sm text-gray-600">Manage NDIS plans, goals, and providers</p>
        </div>
        <Button size="sm">
          <Plus size={16} className="mr-2" />
          Add
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search NDIS..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
        />
      </div>

      {/* Tab Navigation */}
      <div className="grid grid-cols-2 gap-2">
        <Button
          variant={activeTab === 'overview' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('overview')}
        >
          <Accessibility size={16} className="mr-2" />
          Overview
        </Button>
        <Button
          variant={activeTab === 'plans' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('plans')}
        >
          <FileText size={16} className="mr-2" />
          Plans
        </Button>
        <Button
          variant={activeTab === 'goals' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('goals')}
        >
          <Target size={16} className="mr-2" />
          Goals
        </Button>
        <Button
          variant={activeTab === 'providers' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('providers')}
        >
          <Users size={16} className="mr-2" />
          Providers
        </Button>
      </div>

      {/* Content */}
      <div className="space-y-4">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'plans' && renderPlans()}
        {activeTab === 'goals' && renderGoals()}
        {activeTab === 'providers' && renderProviders()}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button variant="outline" className="w-full">
          <Target size={16} className="mr-2" />
          Add Goal
        </Button>
        <Button variant="outline" className="w-full">
          <Users size={16} className="mr-2" />
          Find Provider
        </Button>
      </div>

      {/* Alerts */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle size={16} className="text-blue-600" />
          <h4 className="font-medium text-blue-800">NDIS Reminders</h4>
        </div>
        <div className="space-y-1 text-sm text-blue-700">
          <p>• Plan review due in 2 months</p>
          <p>• Goal progress update needed</p>
        </div>
      </div>
    </div>
  );
} 